import React from "react";
import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight, Layers3, PenTool, Workflow } from "lucide-react";
import { PortfolioMeta } from "./PortfolioMeta";
import { PortfolioVisual } from "./PortfolioVisual";
import { PORTFOLIO_CASE_STUDIES } from "./portfolioData";

export default function CreativePortfolio() {
  return (
    <>
      <PortfolioMeta
        title="Terik Atwell — Creative Director"
        description="Creative Director helping startups grow through branding, websites, KPI dashboards, CRMs, and ticketing systems."
        path="/mycreative"
      />

      <section className="creative-hero">
        <div className="creative-hero-copy">
          <p className="creative-eyebrow">Terik Atwell · Creative Director</p>
          <h1>Creative Direction. <em>Smarter Systems. Built for Growth.</em></h1>
          <p className="creative-lede">
            I help startups grow through clear branding, effective websites, and practical
            business systems—including KPI dashboards, CRMs, and ticketing systems.
          </p>
          <a className="creative-primary-link" href="#selected-work">
            Explore selected work <ArrowDown size={18} aria-hidden="true" />
          </a>
        </div>
        <div className="creative-hero-note" aria-label="Working philosophy">
          <span>01 / POINT OF VIEW</span>
          <p>Clear branding opens the door. Smarter systems keep growth organized.</p>
          <div className="creative-orbit" aria-hidden="true"><i /><i /><i /></div>
        </div>
      </section>

      <section className="creative-capabilities" aria-labelledby="about-title">
        <div>
          <p className="creative-eyebrow">About</p>
          <h2 id="about-title">Creativity that moves the business forward.</h2>
          <p className="creative-about-copy">
            My freelance and client work connects brand, customer experience, and daily
            operations. I build clear experiences that help companies establish their presence,
            measure progress, manage relationships, and organize support.
          </p>
        </div>
        <ul aria-label="Creative direction services">
          <li><PenTool aria-hidden="true" /><span><b>Creative direction & branding</b><small>Clear identity, voice, and visual systems that give a startup a credible presence.</small></span></li>
          <li><Layers3 aria-hidden="true" /><span><b>Effective websites</b><small>Responsive customer experiences that explain the offer and make the next step easy.</small></span></li>
          <li><Workflow aria-hidden="true" /><span><b>KPI, CRM & support systems</b><small>Dashboards, customer workflows, and ticketing systems that connect daily work with business goals.</small></span></li>
        </ul>
      </section>

      <section className="creative-work" id="selected-work" aria-labelledby="work-title">
        <div className="creative-section-heading">
          <div><p className="creative-eyebrow">Selected work</p><h2 id="work-title">Built with purpose.</h2></div>
          <p>Each case study shows how creative direction, customer experience, and practical systems come together in the work.</p>
        </div>

        <div className="creative-project-list">
          {PORTFOLIO_CASE_STUDIES.map((study) => (
            <article className="creative-project" key={study.slug} style={{ "--project-accent": study.accent }}>
              <Link to={`/mycreative/${study.slug}`} className="creative-project-visual" aria-label={`Read ${study.title} case study`}>
                <PortfolioVisual study={study} compact />
              </Link>
              <div className="creative-project-copy">
                <div className="creative-project-meta"><span>{study.number}</span><span>{study.discipline}</span></div>
                {study.statusLabel && <p className="creative-project-status">{study.statusLabel}</p>}
                <h3>{study.title}</h3>
                <p>{study.summary}</p>
                <Link to={`/mycreative/${study.slug}`} className="creative-text-link">
                  Read the case study <ArrowUpRight size={17} aria-hidden="true" />
                </Link>
                {study.links?.length > 0 && (
                  <div className="creative-project-links" aria-label={`${study.title} live links`}>
                    {study.links.map((link) => (
                      <a href={link.url} target="_blank" rel="noreferrer" key={link.url}>
                        {link.label} <ArrowUpRight size={15} aria-hidden="true" />
                      </a>
                    ))}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="creative-closing">
        <p className="creative-eyebrow">Let’s make the next thing matter</p>
        <h2>Clear brands. Effective websites. Smarter systems built for growth.</h2>
        <a href="mailto:sales@metistechlabs.com">Start a conversation <ArrowUpRight size={20} aria-hidden="true" /></a>
      </section>
    </>
  );
}
